// imports...
import {
  Col,
  Row,
  Space,
  Table,
  Typography,
  Input,
  Button,
  DatePicker,
  InputNumber,
  Popconfirm,
  message,
  Tag,
  Select,
} from "antd"
import PageHeader from "../../../components/Elements/PageHeader"
import {
  CopyrightCircleOutlined,
  SearchOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  CarOutlined,
  RollbackOutlined,
} from "@ant-design/icons"
import { useEffect, useState, useRef, useMemo } from "react"
import { useCollection } from "../../../hooks/useCollection"
import { collection, onSnapshot, doc, deleteDoc, writeBatch } from "firebase/firestore"
import { db } from "../../../firebase/config"
import moment from "moment"
import CreateMultipleStoreRolls from "./CreateMultipleStoreRolls"
import ImportRollsFile from "./ImportRollsFile"
import RollFormDrawer from "./CreateNewRoll"
import PalletEditPopover from "./PalletEditPopover"
import { useAuthContext } from "../../../hooks/useAuthContext"
import PalletLocationsDrawer from "./PalletLocationsDrawer"

const { Text } = Typography
const { RangePicker } = DatePicker
const { Option } = Select

// --- 1. The status ranking function remains the same ---
const getStatusRank = (record) => {
  // NEW: Assign the highest rank to refunded rolls
  if (record.backForRefund) {
    return 0 // Top: Refunded
  }
  if (record.shipping || record.inOrder) {
    return 1 // Second: Shipping or In Order
  }
  if (record.verified) {
    return 2 // Third: In Stock
  }
  return 3 // Bottom: Arriving in Stock
}

export default function RollStoreManagement() {
  const { data: materialData, loading: materialLoading } = useCollection("materials")
  const { data: palletLocations, loading: palletLocationsLoading } =
    useCollection("palletLocations")
  const [rollsLoading, setRollsLoading] = useState(true)
  const [rollsData, setRollsData] = useState([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [newPalletLocation, setNewPalletLocation] = useState(null)

  const { admin, warehouse } = useAuthContext()

  const searchInputRef = useRef(null)

  useEffect(() => {
    setRollsLoading(true)
    const rollRef = collection(db, "rollStore")
    const unsubscribe = onSnapshot(rollRef, (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        results.push({ ...doc.data(), id: doc.id })
      })

      // --- 2. Sort the data array here, before setting the state ---
      results.sort((a, b) => getStatusRank(a) - getStatusRank(b))

      setRollsData(results)
      setRollsLoading(false)
    })
    return () => unsubscribe()
  }, [])

  // ... all your handler and filter functions remain the same ...
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
  }

  const handleReset = (clearFilters, confirm) => {
    clearFilters()
    confirm()
  }

  const handleRangeFilter = (dataIndex, values, confirm) => {
    confirm()
  }

  const handleRangeReset = (dataIndex, clearFilters, confirm) => {
    clearFilters()
    confirm()
  }

  const handleDeleteRoll = async (rollId) => {
    try {
      const rollDocRef = doc(db, "rollStore", rollId)
      await deleteDoc(rollDocRef)
      message.success("Roll deleted successfully!")
    } catch (error) {
      console.error("Error deleting roll: ", error)
      message.error("Failed to delete roll. Please try again.")
    }
  }

  const handleDeleteSelectedRolls = async () => {
    if (selectedRowKeys.length === 0) {
      message.info("No rolls selected.")
      return
    }

    try {
      const batch = writeBatch(db)
      selectedRowKeys.forEach((rollId) => {
        const rollDocRef = doc(db, "rollStore", rollId)
        batch.delete(rollDocRef)
      })
      await batch.commit()
      message.success(`${selectedRowKeys.length} rolls deleted successfully!`)
      setSelectedRowKeys([])
    } catch (error) {
      console.error("Error deleting selected rolls: ", error)
      message.error("Failed to delete selected rolls. Please try again.")
    }
  }

  const getLocationNumber = (loc) => {
    if (!loc) return -Infinity
    const match = loc.match(/\d+$/)
    return match ? parseInt(match[0], 10) : -Infinity
  }

  const handleUpdateSelectedRollsLocation = async () => {
    if (!newPalletLocation) {
      message.error("Please select a new pallet location.")
      return
    }
    if (selectedRowKeys.length === 0) {
      message.info("No rolls selected.")
      return
    }

    try {
      const batch = writeBatch(db)
      selectedRowKeys.forEach((rollId) => {
        const rollDocRef = doc(db, "rollStore", rollId)
        batch.update(rollDocRef, { palletLocation: newPalletLocation })
      })
      await batch.commit()
      message.success(`${selectedRowKeys.length} rolls moved to ${newPalletLocation} successfully!`)
      setSelectedRowKeys([])
      setNewPalletLocation(null)
    } catch (error) {
      console.error("Error updating selected rolls' location: ", error)
      message.error("Failed to update locations. Please try again.")
    }
  }

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  }

  const hasSelected = selectedRowKeys.length > 0

  const getColumnSearchProps = (dataIndex, columnTitle) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInputRef}
          placeholder={`Search ${columnTitle}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}>
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const keys = Array.isArray(dataIndex) ? dataIndex : [dataIndex]
      let data = record
      for (let key of keys) {
        if (data && typeof data === "object" && key in data) {
          data = data[key]
        } else {
          data = undefined
          break
        }
      }
      return data ? data.toString().toLowerCase().includes(value.toLowerCase()) : false
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInputRef.current?.select(), 100)
      }
    },
  })

  const getColumnNumberRangeFilterProps = (dataIndex, columnTitle) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
      const currentRange = selectedKeys[0] || [null, null]
      return (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Space direction="vertical">
            <Input.Group compact>
              <InputNumber
                style={{ width: 100, textAlign: "center" }}
                placeholder="Min"
                value={currentRange[0]}
                onChange={(value) =>
                  setSelectedKeys(
                    value !== null || currentRange[1] !== null ? [[value, currentRange[1]]] : []
                  )
                }
              />
              <Input
                className="site-input-split"
                style={{ width: 30, borderLeft: 0, borderRight: 0, pointerEvents: "none" }}
                placeholder="~"
                disabled
              />
              <InputNumber
                style={{ width: 100, textAlign: "center" }}
                placeholder="Max"
                value={currentRange[1]}
                onChange={(value) =>
                  setSelectedKeys(
                    currentRange[0] !== null || value !== null ? [[currentRange[0], value]] : []
                  )
                }
              />
            </Input.Group>
            <Space>
              <Button
                type="primary"
                onClick={() => handleRangeFilter(dataIndex, selectedKeys[0], confirm)}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
                disabled={
                  !selectedKeys[0] || (selectedKeys[0][0] === null && selectedKeys[0][1] === null)
                }>
                Filter
              </Button>
              <Button
                onClick={() => handleRangeReset(dataIndex, clearFilters, confirm)}
                size="small"
                style={{ width: 90 }}>
                Reset
              </Button>
            </Space>
          </Space>
        </div>
      )
    },
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const recordValue = record[dataIndex]
      if (recordValue === null || recordValue === undefined) return false
      const [min, max] = value
      if (min === null && max === null) return true
      if (min !== null && max !== null) return recordValue >= min && recordValue <= max
      if (min !== null) return recordValue >= min
      if (max !== null) return recordValue <= max
      return true
    },
  })

  const orderFilters = useMemo(() => {
    const allNums = rollsData
      .map((r) => r.fullOrderNumber)
      .filter((num) => typeof num === "string" && num.length > 0)
    const uniqueNums = [...new Set(allNums)].sort()
    return uniqueNums.map((ord) => ({
      text: ord,
      value: ord,
    }))
  }, [rollsData])

  const statusColumnFilters = useMemo(() => {
    const staticFilters = [
      { text: "In Stock", value: "FILTER_STATUS_IN_STOCK" },
      { text: "Arriving in Stock", value: "FILTER_STATUS_ARRIVING" },
      { text: "Shipping", value: "FILTER_STATUS_SHIPPING" },
    ]
    const dynamicOrderFilters = orderFilters.map((of) => ({
      text: `Order: ${of.text}`,
      value: of.value,
    }))
    return [...staticFilters, ...dynamicOrderFilters]
  }, [orderFilters])

  const materialFilters = useMemo(() => {
    if (rollsLoading || !rollsData.length) return []
    const uniqueMaterials = [
      ...new Set(rollsData.map((item) => item.material?.material).filter(Boolean)),
    ]
    return uniqueMaterials.map((materialName) => ({
      text: materialName,
      value: materialName,
    }))
  }, [rollsData, rollsLoading])

  const rollsColumns = [
    {
      title: "Status",
      key: "rollStatus",
      align: "center",
      width: 200,
      fixed: "left",
      render: (text, record) => {
        // NEW: Check for refunded status first
        if (record.backForRefund) {
          return (
            <Tag icon={<RollbackOutlined />} color="geekblue">
              Refunded
            </Tag>
          )
        }
        if (record.shipping) {
          return (
            <Tag icon={<CarOutlined />} color="#eb2f96">
              Shipping{record.fullOrderNumber ? ` (${record.fullOrderNumber})` : ""}
            </Tag>
          )
        }
        if (!record.verified) {
          return (
            <Tag icon={<ClockCircleOutlined />} color="processing">
              Arriving in Stock
            </Tag>
          )
        }
        if (record.inOrder) {
          return (
            <Tag icon={<ExclamationCircleOutlined />} color="warning">
              In Order ({record.fullOrderNumber || `Order ID #${record.orderId?.slice(-4)}`})
            </Tag>
          )
        }
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            In Stock
          </Tag>
        )
      },
      filters: statusColumnFilters,
      onFilter: (value, record) => {
        switch (value) {
          case "FILTER_STATUS_REFUNDED": // Make sure to add this value to your statusColumnFilters array
            return !!record.backForRefund
          case "FILTER_STATUS_IN_STOCK":
            // Ensure "In Stock" doesn't include refunded rolls
            return record.verified && !record.inOrder && !record.backForRefund
          case "FILTER_STATUS_ARRIVING":
            return !record.verified
          default:
            return record.inOrder && record.fullOrderNumber === value
        }
      },
      filterSearch: true,
    },
    {
      title: "Location",
      dataIndex: "palletLocation",
      align: "center",
      ...getColumnSearchProps("palletLocation", "Location"),
      sorter: (a, b) => getLocationNumber(b.palletLocation) - getLocationNumber(a.palletLocation),
      render: (_, record) => <PalletEditPopover record={record} />,
    },
    {
      title: "Material",
      dataIndex: ["material", "material"],
      align: "center",
      filters: materialFilters,
      onFilter: (value, record) => record.material?.material === value,
      filterSearch: true,
    },
    {
      title: "Barcode",
      dataIndex: "barcode",
      width: "18%",
      align: "center",
      ...getColumnSearchProps("barcode", "Barcode"),
    },
    {
      title: "Meters",
      dataIndex: "remainingMeters",
      align: "center",
      sorter: (a, b) => a.remainingMeters - b.remainingMeters,
      ...getColumnNumberRangeFilterProps("remainingMeters", "Meters"),
    },
    {
      title: "Width",
      dataIndex: "rollWidth",
      align: "center",
      sorter: (a, b) => a.rollWidth - b.rollWidth,
      ...getColumnNumberRangeFilterProps("rollWidth", "Width (mm)"),
    },
    {
      title: "Added",
      dataIndex: "created",
      align: "center",
      render: (value) => (
        <Text>
          {value?.seconds ? moment.unix(value.seconds).format("DD/MM/YYYY HH:mm") : "N/A"}
        </Text>
      ),
      sorter: (a, b) => (a.created?.seconds || 0) - (b.created?.seconds || 0),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <RangePicker
            value={selectedKeys[0]}
            onChange={(dates) => setSelectedKeys(dates ? [dates] : [])}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={confirm}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}>
              Filter
            </Button>
            <Button
              onClick={() => {
                clearFilters && clearFilters()
                confirm()
              }}
              size="small"
              style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) => {
        if (!record.created?.seconds || !value || value.length !== 2) return true
        const recordDate = moment.unix(record.created.seconds)
        return recordDate.isBetween(value[0], value[1], "days", "[]")
      },
    },
    {
      title: "Price (€)",
      dataIndex: "price",
      align: "center",
      hidden: warehouse,
      render: (price) => {
        if (typeof price === "number") {
          return `€${price.toFixed(2)}`
        }
        return "N/A"
      },
      sorter: (a, b) => (a.price || 0) - (b.price || 0),
      ...getColumnNumberRangeFilterProps("price", "Price"),
    },
    {
      title: "Action",
      align: "center",
      key: "action",
      hidden: !(admin || warehouse),
      fixed: "right",
      width: 100,
      render: (text, record) => (
        <Space>
          {admin && (
            <Popconfirm
              title="Delete this roll?"
              description={`Are you sure you want to delete roll with barcode ${
                record.barcode || record.id
              }?`}
              onConfirm={() => handleDeleteRoll(record.id)}
              okText="Yes, Delete"
              okType="danger"
              cancelText="No"
              placement="leftTop">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
          {(admin || warehouse) && (
            <RollFormDrawer
              materialData={materialData}
              materialLoading={materialLoading}
              forEditing={true}
              record={record}
            />
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader icon={<CopyrightCircleOutlined />} text="Roll Management" />
        </Col>

        <Col span={24}>
          <Space wrap>
            {admin && (
              <>
                <RollFormDrawer materialData={materialData} materialLoading={materialLoading} />
                <CreateMultipleStoreRolls
                  materialData={materialData}
                  materialLoading={materialLoading}
                />
                <ImportRollsFile />
              </>
            )}

            {(admin || warehouse) && <PalletLocationsDrawer />}

            {hasSelected && (
              <>
                {(admin || warehouse) && (
                  <>
                    <Select
                      style={{ width: 200 }}
                      placeholder="Select new location"
                      onChange={(value) => setNewPalletLocation(value)}
                      loading={palletLocationsLoading}
                      value={newPalletLocation}>
                      {[...(palletLocations || [])]
                        .sort((a, b) => getLocationNumber(b.name) - getLocationNumber(a.name))
                        .map((loc) => (
                          <Option key={loc.id} value={loc.name}>
                            {loc.name}
                          </Option>
                        ))}
                    </Select>
                    <Button
                      type="primary"
                      onClick={handleUpdateSelectedRollsLocation}
                      disabled={!newPalletLocation}>
                      Update Location
                    </Button>
                  </>
                )}

                {admin && (
                  <Popconfirm
                    title="Delete selected rolls?"
                    description={`Are you sure you want to delete the ${selectedRowKeys.length} selected rolls?`}
                    onConfirm={handleDeleteSelectedRolls}
                    okText="Yes, Delete"
                    okType="danger"
                    cancelText="No">
                    <Button danger icon={<DeleteOutlined />}>
                      Delete Selected
                    </Button>
                  </Popconfirm>
                )}
              </>
            )}
          </Space>
        </Col>

        <Col span={24}>
          <Table
            rowSelection={rowSelection}
            dataSource={rollsData}
            loading={rollsLoading}
            rowKey={(record) => record.id}
            columns={rollsColumns}
            size="small"
            pagination={false}
            scroll={{ x: 1300 }}
          />
        </Col>
      </Row>
    </div>
  )
}
